import { useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeftRight, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { ExchangeRate } from '@nucleus-commerce/api-client/platform';
import { Button } from '@nucleus-commerce/ui/components/button';

import { canManageExchangeRates } from '@/shared/session/roles';
import { session } from '@/shared/session/session';

import { pairLabel, rowFromRate } from '../api/mappers';
import { useExchangeRateColumns } from '../components/exchange-rate-columns';
import { exchangeRateFilters } from '../components/exchange-rate-filters';
import { UpsertRatesDialog } from '../components/upsert-rates-dialog';
import { useExchangeRates } from '../hooks/use-exchange-rates';
import type { ExchangeRateListSearch } from '../schemas/exchange-rate-list-search';
import { emptyRateRow, type RateRowInput } from '../schemas/rate-rows-schema';

const ROUTE_ID = '/_authed/exchange-rates' as const;
const ROUTE_PATH = '/exchange-rates' as const;
const EMPTY: readonly ExchangeRate[] = [];

function ExchangeRatesList() {
  const { t } = useTranslation('exchangeRates');
  const { me } = session.useSession();
  const canManage = canManageExchangeRates(me?.user.role);
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const patchSearch = useCallback(
    (patch: SearchPatch) =>
      void navigate({
        search: (prev) => ({ ...prev, ...(patch as Partial<ExchangeRateListSearch>) }),
        replace: true,
      }),
    [navigate],
  );

  const query = useExchangeRates();
  const rates = query.data?.items ?? EMPTY;
  const [dialogRows, setDialogRows] = useState<readonly RateRowInput[] | null>(null);

  const openBlank = useCallback(
    () => setDialogRows([emptyRateRow(rates[0]?.baseCurrency ?? 'USD')]),
    [rates],
  );
  const openFor = useCallback((rate: ExchangeRate) => setDialogRows([rowFromRate(rate)]), []);
  const columns = useExchangeRateColumns({ onUpdate: canManage ? openFor : null });
  const filters = useMemo(() => exchangeRateFilters(t, rates), [rates, t]);
  const list = useSearchListState({ search, onSearchChange: patchSearch, filters });
  const table = useDataTable({
    data: rates,
    columns,
    filters,
    list,
    getRowId: (row) => `${row.baseCurrency}/${row.quoteCurrency}`,
  });

  const isEmptyList = query.data?.items.length === 0;
  const addButton = canManage ? (
    <Button onClick={openBlank} data-tour="platform.exchange-rates.upsert">
      <Plus aria-hidden />
      {t('upsert')}
    </Button>
  ) : undefined;

  return (
    <PageContent>
      <PageHeader
        title={<span className="font-serif text-[1.875rem] font-normal">{t('title')}</span>}
        description={t('description')}
        actions={isEmptyList ? undefined : addButton}
      />
      {isEmptyList ? (
        <EmptyState
          tour="platform.exchange-rates.empty"
          tourLink
          icon={ArrowLeftRight}
          title={t('empty.title')}
          description={t('empty.description')}
          action={addButton}
        />
      ) : (
        <DataTable
          pillFilter="source"
          table={table}
          search={{ label: t('search'), placeholder: t('search') }}
          getRowLabel={pairLabel}
          resourceName={{ singular: t('resource_one'), plural: t('resource_other') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          tour={{
            table: 'platform.exchange-rates.table',
            search: 'platform.exchange-rates.search',
          }}
        />
      )}
      {canManage ? (
        <UpsertRatesDialog rows={dialogRows} onClose={() => setDialogRows(null)} />
      ) : null}
    </PageContent>
  );
}

export function ExchangeRatesPage() {
  return (
    <FeatureErrorBoundary name="exchangeRates.list">
      <ExchangeRatesList />
    </FeatureErrorBoundary>
  );
}
